import React, { useState, useCallback, Fragment, useMemo, useEffect } from 'react';
import {
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Progress,
    UncontrolledTooltip,
    CustomInput,
    InputGroup,
    Badge,
    Dropdown
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import styled from 'styled-components';

import {
    useCancellablePromises,
    cancellablePromise,
    delay,
    getDateDiffInDays,
    truncate,
    parseDate,
    initialLetters
} from 'utils';
import { fetchContractList } from 'api/actions/contracts'
import apiClient from 'api/client';
import { useContractListState, useContractList, OPEN_CONTRACT_DETAIL, SET_CURRENT_CONTRACT, SET_SELECTED_CONTRACTS, UPDATE_CONTRACT, SET_MODAL, OPEN_CREATE_CONTRACT } from 'context/contracts'
import useContractActions from 'hooks/useContractActions';
import Whoami from 'actions/whoami-react';
// import Can from 'utils/Can';
import { useProfileState, useProfileAbility } from 'context/profile';
import TratoLoader from 'components/Shared/TratoLoader';
import { Circle } from 'components/Sign/SignView';

function getBadgeColor(days, status) {
    if (['RECEIVED_SIGNED', 'SENT_TO_DOWNLOAD', 'FINALIZED'].includes(status)) return 'lighter-gray';
    if (days <= 2)
        return 'success';
    if (days >= 3 && days <= 4)
        return 'warning';
    else
        return 'danger';
}

function MenuDropdown({ contract }) {
    const { t } = useTranslation();
    const [state, dispatch] = useContractList();
    const availableActions = useContractActions(contract, null, {
        listOnly: state.meta.listOnly,
        refetch: state.query,
        onUpdateContract: (newContract) => {
            dispatch({
                type: UPDATE_CONTRACT, contract: {
                    ...contract,
                    ...newContract
                }
            })
        },
        onOpenModal: ({ name }) => {
            dispatch({ type: SET_CURRENT_CONTRACT, contract })
            dispatch({ type: SET_MODAL, name })
        },
        onOpenCreateContract: ({ referenceId, options }) => {
            // dispatch({ type: SET_CURRENT_CONTRACT, referenceId })
            dispatch({ type: OPEN_CREATE_CONTRACT, referenceId, options })
        },
    })

    return (
        <DropdownMenu tag="ul" right>
            {
                availableActions.map((action, i) => {
                    const { faIcon, label, onClick } = action;
                    return (
                        <DropdownItem key={`${action.name}-${i}`} href="#" onClick={onClick} tag="li" className="contract-dropdown-item">
                            <i className={`fas fa-${faIcon} mr-2`}></i>{t(label)}  <br />
                        </DropdownItem>
                    )
                })
            }
        </DropdownMenu>
    )
}

function ContractItemContainer(props) {

    const {
        contract,
        columns } = props;
    const history = useHistory();
    const [isChildOpen, setIsChildOpen] = useState(!!contract.childs?.length || false);
    const [state, dispatch] = useContractList()

    const api = useCancellablePromises();

    const handleClick = (contract) => {
        api.clearPendingPromises();
        const waitForClick = cancellablePromise(delay(500));
        api.appendPendingPromise(waitForClick);

        return waitForClick.promise
            .then(() => {
                api.removePendingPromise(waitForClick);
                onClick(contract);
            })
            .catch(errorInfo => {
                api.removePendingPromise(waitForClick);
                if (!errorInfo.isCanceled) {
                    throw errorInfo.error;
                }
            });
    };

    const handleDoubleClick = (contract) => {
        api.clearPendingPromises();
        history.push(`/contracts/editor/${contract._id}`)
    };

    const onClick = (contract) => {
        dispatch({ type: SET_CURRENT_CONTRACT, contract })
        dispatch({ type: OPEN_CONTRACT_DETAIL })
        try {
            // const sidebar = app.request('contractSidebar');
            // sidebar.show(new ContractDetail({ contract, listOnly: false })); //TODO: CHANGE
        } catch (error) {
            console.error({ error })
        }

    }

    const handleSelectChange = (event) => {
        // event.preventDefault();
        event.stopPropagation();
        api.clearPendingPromises();
        const val = event.target.value;
        const values = state.selectedContracts.includes(val) ? state.selectedContracts.filter(v => val !== v) : [...state.selectedContracts, val]
        dispatch({ type: SET_SELECTED_CONTRACTS, selected: values });
    }

    async function fetchChildContracts({ parentReferenceId, references }) {
        try {
            const fetchedContracts = await apiClient(fetchContractList({ parentReferenceId, limit: 10, url: '/contracts/all' }))
            dispatch({
                type: UPDATE_CONTRACT, contract: {
                    _id: parentReferenceId,
                    childs: fetchedContracts.data.docs.map(doc => ({ ...doc, isChild: true }))
                }
            })
        } catch (error) {
            console.error(error)
        }
    }


    const handleToggleChild = (e) => {
        e.stopPropagation();
        setIsChildOpen(open => !open);
        if (!isChildOpen) {
            fetchChildContracts({ parentReferenceId: contract._id, references: contract.reference.map(ref => ref.document).join('|') })
        }
    }

    useEffect(() => {
        setIsChildOpen(!!contract.childs?.length)
    }, [contract])

    return (
        <>
            <ContractItem
                isReadOnly={state.meta.listOnly && !state.meta.searchTerm}
                isActive={state.currentContract._id === contract._id}
                isChecked={state.selectedContracts.includes(contract._id)}
                columns={columns}
                contract={contract}
                onDoubleClick={handleDoubleClick}
                onSelectChange={handleSelectChange}
                showFetchChilds={state.meta.searchTerm !== 'daysToExpire'}
                onClick={handleClick}
                isChildOpen={isChildOpen}
                onToggleChild={handleToggleChild}
            />
            {isChildOpen &&
                contract.childs?.map(contractChild => (
                    <ContractItem
                        key={contractChild._id}
                        isChild
                        isActive={state.currentContract._id === contractChild._id}
                        columns={columns}
                        contract={contractChild}
                        onDoubleClick={handleDoubleClick}
                        onSelectChange={handleSelectChange}
                        onClick={handleClick}
                    />
                ))
            }
        </>
    )
}

const Avatar = styled.div`
    text-align: center;
    border-radius: 50%;
    border: 1px solid;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 35px; 
    height: 35px;
    background-color: ${props => props.backgroundColor};
    border-color: ${props => props.borderColor};
    color: ${props => props.color};
`

const ParticipantAvatar = ({ className, contractId, participant, index }) => {
    const { t } = useTranslation() 
    const { backgroundColor, borderColor, color } = getColors(participant.status)

    return (
        <div className={clsx("d-inline-flex", className)}>
            <Avatar 
                id={`participant-${participant._id}-${contractId}`}
                className={'align-self-center align-middle p-2 mr-1'} 
                backgroundColor={backgroundColor}
                borderColor={borderColor} 
                color={color}  
            >
                <small>
                    <b>
                        {initialLetters(participant.name, participant.last_name) || `P${index}`}
                    </b>
                    {/* {icon && <i className="fas fa-user-plus"></i>} */}
                </small>
            </Avatar>
            <UncontrolledTooltip target={`participant-${participant._id}-${contractId}`}>
                <div className="text-capitalize">
                    {participant.name} {participant.last_name}
                </div> 
                <div>
                    {t(`contracts.status_v2.${participant.status || ''}`,)}
                </div>
            </UncontrolledTooltip>
        </div>
    ) 
}

const getColors = (status) => {

    // ACCEPTED_NEGOTIATION = 'ACCEPTED_NEGOTIATION',
    // NEGOTIATION_TAKEN = 'NEGOTIATION_TAKEN',
    // PENDING_NEGOTIATION = 'PENDING_NEGOTIATION', #F47B33
    switch(status) {
        case 'NEGOTIATION_TAKEN':
            return { backgroundColor: 'white', borderColor: '#F7AA19', color: 'black' }
        case 'PENDING_NEGOTIATION':
            return { backgroundColor: 'white', borderColor: '#393d42', color: 'black' }
        case 'ACCEPTED_NEGOTIATION':
            return { backgroundColor: 'white', borderColor: '#24A047', color: 'black' }
        case 'RECEIVED_TO_SIGN':
        case 'RECEIVED_TO_FILL':
            return { backgroundColor: '#F7AA19', borderColor: '#F7AA19', color: 'white' }
        case 'SENT_FILLED':
        case 'RECEIVED_SIGNED':
        case 'SENT_SIGNED': 
        case 'RECEIVED_TO_DOWNLOAD':
        case 'SENT_TO_DOWNLOAD':
            return { backgroundColor: '#24A047', borderColor: '#24A047', color: 'white' }
        default:
            return { backgroundColor: '#ccc', borderColor: '#ccc', color: 'black' }
    }
}

const MAX_NUMBER_OF_PARTICIPANTS = 4;

function ContractItem({ contract, isActive, isChild, isChecked, isReadOnly, columns = [], showFetchChilds = true, onDoubleClick, onClick, onSelectChange, isChildOpen, onToggleChild }) {
    const { t } = useTranslation();
    const { profile, roles } = useProfileState()
    const history = useHistory();
    const [isParticipantsMenuOpen, setIsParticipantsMenuOpen] = useState(false);
    const profileAbility = useProfileAbility();
    const whoami = useMemo(() => {
        return new Whoami(contract, profile.email, profile._id, roles, profile.organization, profile.groups);
    }, [contract, profile, roles])
    const diffInDays = useMemo(() => {
        let startDate = contract.createdAt;
        let endDate = contract.statusDate || new Date();
        if (contract.statusHistory?.length) {
            const lastStatusHistory = contract.statusHistory[contract.statusHistory.length - 1]
            startDate = lastStatusHistory.endedAt;
            endDate = new Date();
            if (['RECEIVED_SIGNED', 'SENT_TO_DOWNLOAD', 'FINALIZED'].includes(contract.status)) {
                startDate = contract.createdAt;
                endDate = lastStatusHistory.endedAt;
            }
        }
        
        return getDateDiffInDays(startDate, endDate)
    }, [contract.createdAt, contract.statusDate, contract.statusHistory])
    const image = useMemo(() => {
        return contract.importedPdf ? '/images/listing/pdf_document.png' :
            contract.wordonline ? '/images/listing/word_document.png' :
                '/images/listing/document.png';
    }, [contract.importedPdf, contract.wordonline])
    const voteStatus = useMemo(() => {
        return whoami.getVoteStatus()
    }, [whoami])

    const handleClick = useCallback(() => {
        onClick(contract)
    }, [contract, onClick])
    const handleDoubleClick = useCallback(() => {
        onDoubleClick(contract)
    }, [contract, onDoubleClick])

    const hasChilds = showFetchChilds && !!contract?.reference?.length;

    const goToFolder = ({ folder }) => {
        history.push(`/contracts/list/${folder._id}`)
    }

    const contractStatus = useMemo(() => {
        if (contract.isApplication && contract.status === '') {
            return t('contracts.status.RECEIVED')
        }

        return t(`contracts.status.${contract.status}`)
    }, [contract.status, contract.isApplication, t])

    const toggleParticipantsMenu = (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        setIsParticipantsMenuOpen(open => !open)
    }

    const [participants, participantsInMenu, participantsLength] = useMemo(() => {
        const parts = contract.participants.map((p, i) => ({ ...p, index: i + 1 })).filter(p  => {
            if (contract.status === 'NEGOTIATION') return !p.isNotNegotiator;
            if (['SENT_TO_SIGN'].includes(contract.status)) return !p.isNotSigner;
            return true;
        });
        return [parts.slice(0, MAX_NUMBER_OF_PARTICIPANTS), parts.slice(MAX_NUMBER_OF_PARTICIPANTS), parts.length]
    }, [contract.participants, contract.status])
    
    return (
        <tr className={clsx('align-items-center pointer contract-item', isActive && 'indigo-10', isActive && isChild && 'indigo-10')} onDoubleClick={handleDoubleClick} onClick={handleClick}>
            <td className="align-middle align-items-center">
                {!isChild && !isReadOnly && <CustomInput type="checkbox" name="list-item" value={contract._id} id={contract._id} onChange={onSelectChange} checked={isChecked} />}
            </td>

            <td onClick={e => { e.stopPropagation() }} className="align-middle align-items-center">
                <div className="align-middle align-items-center" style={{ display: 'inline-flex' }}>
                    <img height="30px" src={image} alt="img" style={isChild && {position: "relative",left: "50px"}} />
                </div>
            </td>

            <td className="text-left text-nowrap text-truncate align-middle">
                <UncontrolledTooltip placement="top" target={`contract-name-header${contract._id}`}>
                    {contract.name}
                </UncontrolledTooltip>
                <span className={`d-block text-truncate ${isChild && 'ml-5'}`} id={`contract-name-header${contract._id}`} >
                    {contract.locked && (
                        <span className="text-dark mr-2" role="button">
                            <i className="fas fa-lock"></i>
                        </span>
                    )}
                    {!isChild && !!hasChilds && (
                        <Fragment>
                            <span className="text-info mr-1" role="button">
                                <i className="fas fa-link"></i>
                            </span>
                            <span className="text-info mr-1" name="child-button" role="button"><i className={`fas text-purple fa-${isChildOpen ? 'minus' : 'plus'}`} id="5eb07017c6794c59d0f94dcc" onClick={onToggleChild}></i></span>
                        </Fragment>
                    )}
                    {contract.childOf && contract.childOf._id && <i className="fas fa-folder mr-1" style={{ color: 'burlywood' }} onClick={() => goToFolder({ folder: contract.childOf })}></i>}
                    {contract.documentId ? `${contract.documentId} - ` : ''}{contract.name}
                </span>
                <small className={`text-muted ${isChild && 'ml-5'}`}>
                    {contract.type?.name}
                       - {t('contracts.listing.updated')} {parseDate(contract.updatedAt, 'P')}
                </small>
            </td>
            {columns.includes('milestone') && profileAbility.can('read', 'Contract', 'milestones') &&
                <td className="text-center align-middle d-none d-lg-table-cell">
                    <MilestoneProgressBar contract={contract} />
                </td>
            }
            {columns.includes('creator') &&
                <td className="align-middle text-nowrap text-truncate">
                    {contract?.creator?.givenName} {contract?.creator?.familyName}

                </td>
            }
            {columns.includes('user') &&
                <td className="align-middle text-nowrap text-truncate d-none d-lg-table-cell">

                    {contract?.user?.givenName} {contract?.user?.familyName}

                </td>
            }
            {columns.includes('participants') &&
                <td className="align-middle">
                    <div className="d-flex justify-content-center align-items-center ">                   
                    {participants.map((participant) => {
                        return (
                            <ParticipantAvatar key={`${contract._id}-${participant._id}`} contractId={contract._id} participant={participant} index={participant.index} />
                        ) 
                    })}
                    {participantsLength > MAX_NUMBER_OF_PARTICIPANTS && 
                        <Dropdown isOpen={isParticipantsMenuOpen} toggle={toggleParticipantsMenu}>
                                <DropdownToggle tag="div" className="d-inline-flex">
                                    <Avatar 
                                        className={'align-self-center align-middle p-3 mr-1'} 
                                        backgroundColor={'#ccc'}
                                        borderColor={'#ccc'} 
                                        color={'black'} 
                                    >
                                        <small>
                                            <b>
                                                {contract.participants.length - MAX_NUMBER_OF_PARTICIPANTS}
                                                <strong>+</strong>
                                            </b>
                                        </small>
                                    </Avatar>
                                </DropdownToggle>
                                <DropdownMenu className="p-2" style={{maxWidth: 110}}>
                                    <div className="d-flex flex-wrap m-1 justify-content-center align-items-center">
                                        {participantsInMenu.map((participant, i) => (
                                            <ParticipantAvatar className="mb-1" contractId={contract._id} participant={participant} index={participant.index} />
                                        ))}
                                    </div>                  
                                </DropdownMenu>
                        </Dropdown>
                    }
                    </div> 
                </td>
            }
            {columns.includes('entered') &&
                <td className="align-middle"></td>
            }
            {columns.includes('created') &&
                <td className="align-middle text-truncate">
                    <span>{t('templates.listing.created')}</span><br></br>
                    <span>{parseDate(contract.createdAt, 'P')}</span>
                </td>
            }
            {columns.includes('days') &&
                <td className="align-middle d-none d-lg-table-cell">
                    <Badge
                        className="d-inline px-3"
                        color={`${getBadgeColor(diffInDays, contract.status)}`}
                    >
                        {diffInDays}
                    </Badge>
                </td>
            }
            {columns.includes('status') &&
                <td className="align-middle text-truncate">
                    {contractStatus}
                    <div>
                        {
                            voteStatus && t(`voting.${voteStatus}`)
                        }
                    </div>
                    {contract.status === '' && whoami.hasVobo() && (
                        <div>
                            { whoami.everyoneApproved() ?
                                t('contracts.status.APPROVED')
                                : <div>
                                    {t('contracts.status.SENT_TO_VOBO')}
                                    {contract.vobo.map(vobo => {
                                        if (vobo.status === 'sent') {
                                            return (
                                                <div key={vobo._id}>
                                                    {vobo?.userGroup ?
                                                        `( G: ${vobo?.userGroup?.name} )`
                                                        :
                                                        `( ${vobo?.user?.givenName} ${vobo?.user?.familyName} )`
                                                    }
                                                </div>
                                            )
                                        }
                                    })
                                    }
                                </div>
                            }
                        </div>
                    )
                    }
                    {(whoami.isSigned() || whoami.isFinalized()) && whoami.isAboutToExpire(profile?.tenant?.expirationDaysFilter) && (
                        <div>
                            {t('dashboard.counter.daysToExpire')}
                        </div>
                    )
                    }
                </td>
            }
            {columns.includes('signature') &&
                <td className="align-middle d-none d-lg-table-cell">
                    <img height="30px" alt="signature" className="signature-type" src={`/images/listing/${contract.signatureType}.png`} />
                </td>
            }

            <td className="align-middle">
                <UncontrolledDropdown onClick={(e) => e.stopPropagation()}>
                    <DropdownToggle color="light">
                        <i className="fas fa-bars"></i>
                    </DropdownToggle>
                    <MenuDropdown contract={contract} />
                </UncontrolledDropdown>
            </td>
        </tr>
    )
}

function ContractListContainer({ columns }) {
    const { t } = useTranslation();

    const { isLoadingContracts, contracts } = useContractListState();

    const minCols = 4; //Number of columns that cannot be hidden.

    if (isLoadingContracts) {
        return <TratoLoader className="absolute-center" size="xl" />
    }

    if (!contracts || !contracts?.length) {
        return (
            <tr className="text-center">
                <td colSpan={minCols + columns.length}>
                    {t('contracts.listing.empty')}
                </td>
            </tr>
        )
    }

    return (
        contracts?.map((contract) => (
            <ContractItemContainer
                key={contract._id}
                contract={contract}
                columns={columns}
            />
        ))
    )
}

export default ContractListContainer

function MilestoneProgressBar({ contract }) {
    const progress = useMemo(() => {
        const completed = contract.milestones?.reduce((total, milestone) => {
            if (milestone.completed) total += 1
            return total
        }, 0) ?? 0;
        const total = contract.milestones?.length ?? 1
        const percentage = Math.floor((completed / total) * 100);
        return isNaN(percentage) ? 0 : percentage;
    }, [contract.milestones])

    return (
        <div>
            <Progress
                color="primary"
                value={progress ?? 0}
            />
            <small>
                {progress ?? 0}%
            </small>
        </div>
    )
}
